import {
  Alert,
  Chip,
  CircularProgress,
  Divider,
  Stack,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  formApi,
  FormFieldType,
  FormSubmissionStatus,
  GetFormDto,
  GetFormSubmissionDto,
} from 'src/api/form';
import { GetVisitById } from 'woofwoof-api';

type Props = {
  visit: GetVisitById;
};

const statusColors: Record<string, 'default' | 'warning' | 'success'> = {
  not_started: 'default',
  draft: 'warning',
  submitted: 'success',
};

export default function VisitModalFormsTab({ visit }: Props) {
  const { t } = useTranslation();
  const fieldTypeLabels = useMemo(
    (): Record<FormFieldType, string> => ({
      [FormFieldType.TEXT]: String(t('visit.formFieldText')),
      [FormFieldType.SELECT]: String(t('visit.formFieldSelect')),
      [FormFieldType.CHECKBOX]: String(t('visit.formFieldCheckbox')),
      [FormFieldType.RADIO]: String(t('visit.formFieldRadio')),
      [FormFieldType.FILE]: String(t('visit.formFieldFile')),
    }),
    [t],
  );
  const statusLabels = useMemo(
    (): Record<string, string> => ({
      not_started: String(t('visit.formStatusNotStarted')),
      draft: String(t('visit.formStatusDraft')),
      submitted: String(t('visit.formStatusSubmitted')),
    }),
    [t],
  );

  // Fetch submissions for this visit
  const { data: submissionsResponse, isLoading: isSubmissionsLoading } =
    useQuery(['form-submissions', visit.id], () =>
      formApi.getSubmissionsByVisit(visit.id),
    );

  const submissions = submissionsResponse?.data || [];

  // Get required forms from the visit (cast since not in generated types)
  const requiredForms = ((visit as any).requiredForms || []) as Array<{
    formId: string;
    formName: string;
    submissionStatus: string;
  }>;

  // Fetch form definitions for submitted forms
  const formIds = Array.from(
    new Set([
      ...submissions.map((s) => s.formId),
      ...requiredForms.map((f) => f.formId),
    ]),
  );

  const { data: formsResponses, isLoading: isFormsLoading } = useQuery(
    ['forms-by-ids', ...formIds],
    async () => {
      const results = await Promise.all(
        formIds.map((id) => formApi.getFormById(id).catch(() => null)),
      );
      return results.filter(Boolean).map((r) => r!.data);
    },
    { enabled: formIds.length > 0 },
  );

  const formsMap = new Map<string, GetFormDto>();
  (formsResponses || []).forEach((form) => {
    if (form) formsMap.set(form.id, form);
  });

  if (isSubmissionsLoading || isFormsLoading) {
    return (
      <Stack alignItems="center" py={4}>
        <CircularProgress />
      </Stack>
    );
  }

  if (requiredForms.length === 0 && submissions.length === 0) {
    return (
      <Alert severity="info" sx={{ mt: 2 }}>
        {t('visit.noFormsForVisit')}
      </Alert>
    );
  }

  return (
    <Stack spacing={3} sx={{ mt: 2 }}>
      {requiredForms.map((reqForm) => {
        const form = formsMap.get(reqForm.formId);
        const submission = submissions.find(
          (s) => s.formId === reqForm.formId,
        );

        return (
          <Stack
            key={reqForm.formId}
            spacing={2}
            sx={{
              p: 2,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
            }}
          >
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography variant="subtitle1" fontWeight={600}>
                {reqForm.formName}
              </Typography>
              <Chip
                label={statusLabels[reqForm.submissionStatus] || reqForm.submissionStatus}
                color={statusColors[reqForm.submissionStatus] || 'default'}
                size="small"
              />
            </Stack>

            {!submission && (
              <Typography variant="body2" color="text.secondary">
                {t('visit.formNotFilledByPatient')}
              </Typography>
            )}

            {submission && form && (
              <Stack spacing={1.5}>
                {form.fields
                  .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
                  .map((field) => {
                    const response = submission.responses.find(
                      (r) => r.fieldId === field.id,
                    );
                    const value = response?.value;
                    const customValue = response?.customValue;

                    let displayValue = '-';
                    if (value !== undefined && value !== null && value !== '') {
                      if (Array.isArray(value)) {
                        const items = value.filter((v: string) => v !== '__custom__');
                        if (customValue) items.push(customValue);
                        displayValue = items.join(', ') || '-';
                      } else if (value === '__custom__' && customValue) {
                        displayValue = customValue;
                      } else {
                        displayValue = String(value);
                      }
                    }

                    return (
                      <Stack key={field.id}>
                        <Typography variant="caption" color="text.secondary">
                          {field.label}
                          {field.required && ' *'}
                        </Typography>
                        <Typography variant="body2">{displayValue}</Typography>
                      </Stack>
                    );
                  })}
              </Stack>
            )}
          </Stack>
        );
      })}

      {/* Show any submissions not in requiredForms (edge case) */}
      {submissions
        .filter((s) => !requiredForms.some((rf) => rf.formId === s.formId))
        .map((submission) => {
          const form = formsMap.get(submission.formId);
          return (
            <Stack
              key={submission.id}
              spacing={2}
              sx={{
                p: 2,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
              }}
            >
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
              >
                <Typography variant="subtitle1" fontWeight={600}>
                  {form?.name || 'Formularz'}
                </Typography>
                <Chip
                  label={statusLabels[submission.status] || submission.status}
                  color={statusColors[submission.status] || 'default'}
                  size="small"
                />
              </Stack>

              {form && (
                <Stack spacing={1.5}>
                  {form.fields
                    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
                    .map((field) => {
                      const response = submission.responses.find(
                        (r) => r.fieldId === field.id,
                      );
                      const value = response?.value;
                      const customValue = response?.customValue;

                      let displayValue = '-';
                      if (value !== undefined && value !== null && value !== '') {
                        if (Array.isArray(value)) {
                          const items = value.filter(
                            (v: string) => v !== '__custom__',
                          );
                          if (customValue) items.push(customValue);
                          displayValue = items.join(', ') || '-';
                        } else if (value === '__custom__' && customValue) {
                          displayValue = customValue;
                        } else {
                          displayValue = String(value);
                        }
                      }

                      return (
                        <Stack key={field.id}>
                          <Typography variant="caption" color="text.secondary">
                            {field.label}
                            {field.required && ' *'}
                          </Typography>
                          <Typography variant="body2">
                            {displayValue}
                          </Typography>
                        </Stack>
                      );
                    })}
                </Stack>
              )}
            </Stack>
          );
        })}
    </Stack>
  );
}
